export function registerWpformDirectives(Alpine) {
  const readMoreArrowSvg = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.5056 5.20491C11.7789 4.93175 12.2221 4.93164 12.4953 5.20491L18.7949 11.5047C18.926 11.6359 18.9999 11.8142 19 11.9996C18.9999 12.1851 18.9261 12.3634 18.7949 12.4946L12.4953 18.7953C12.2221 19.0683 11.7789 19.0682 11.5056 18.7953C11.2323 18.522 11.2324 18.0788 11.5056 17.8055L16.6103 12.6996H5.69995C5.31351 12.6996 5.00017 12.3861 5 11.9996C5.00019 11.6132 5.31352 11.2997 5.69995 11.2997H16.6103L11.5056 6.19473C11.2323 5.92143 11.2324 5.47828 11.5056 5.20491Z"
        fill="white"
      />
    </svg>
  `;

  const getAnimatedSubmitButton = (form) => {
    const submitBtn = form.querySelector(".wpforms-submit");
    if (!submitBtn) return null;
    if (submitBtn.tagName !== "INPUT") return submitBtn;

    const button = document.createElement("button");
    Array.from(submitBtn.attributes).forEach((attr) => {
      if (attr.name === "value") return;
      button.setAttribute(attr.name, attr.value);
    });
    button.type = submitBtn.type || "submit";
    button.textContent =
      submitBtn.value || submitBtn.getAttribute("value") || "Submit";

    submitBtn.replaceWith(button);
    return button;
  };

  const setupReadMoreHoverAnimation = (el) => {
    if (!window.gsap || el.dataset.readMoreAnimReady === "1") return;

    const textCurrent = el.querySelector(
      ".masked-element:not(.masked-mask):not(.is-horizontal)",
    );
    const textMask = el.querySelector(".masked-element.masked-mask");
    const iconMask = el.querySelector(".masked-element.is-horizontal");

    if (textCurrent) gsap.set(textCurrent, { y: 0, opacity: 1 });
    if (textMask) gsap.set(textMask, { y: 20, opacity: 0 });
    if (iconMask) gsap.set(iconMask, { x: 0 });

    el.addEventListener("mouseenter", () => {
      if (textCurrent) {
        gsap.to(textCurrent, {
          y: -20,
          opacity: 0,
          duration: 1,
          ease: "power4.out",
          overwrite: "auto",
        });
      }
      if (textMask) {
        gsap.to(textMask, {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power4.out",
          overwrite: "auto",
        });
      }
      if (iconMask) {
        gsap.to(iconMask, {
          x: 6,
          duration: 0.5,
          ease: "power2.out",
          overwrite: "auto",
        });
      }
    });

    el.addEventListener("mouseleave", () => {
      if (textCurrent) {
        gsap.to(textCurrent, {
          y: 0,
          opacity: 1,
          duration: 1.1,
          ease: "power4.out",
          overwrite: "auto",
        });
      }
      if (textMask) {
        gsap.to(textMask, {
          y: 20,
          opacity: 0,
          duration: 1.1,
          ease: "power4.out",
          overwrite: "auto",
        });
      }
      if (iconMask) {
        gsap.to(iconMask, {
          x: 0,
          duration: 0.5,
          ease: "power2.out",
          overwrite: "auto",
        });
      }
    });

    el.dataset.readMoreAnimReady = "1";
  };

  const setupReadMoreSubmitMarkup = (submitBtn) => {
    if (!submitBtn) return;

    const escapeHtml = (text) =>
      (text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const submitLabel =
      submitBtn.dataset.submitLabel ||
      submitBtn.textContent?.trim() ||
      submitBtn.getAttribute("value") ||
      "Submit";
    const safeSubmitLabel = escapeHtml(submitLabel);

    submitBtn.dataset.submitLabel = submitLabel;

    if (submitBtn.dataset.readMoreMarkupReady !== "1") {
      submitBtn.innerHTML = `
        <span class="wpforms-submit__label relative overflow-hidden block">
          <span class="wpforms-submit__text body-def font-semibold masked-element block">${safeSubmitLabel}</span>
          <span class="wpforms-submit__text body-def font-semibold masked-element masked-mask block">${safeSubmitLabel}</span>
        </span>
        <span class="wpforms-submit__icon masked-element is-horizontal">
          ${readMoreArrowSvg}
        </span>
      `;
      submitBtn.dataset.readMoreMarkupReady = "1";
    }

    submitBtn.classList.add("wpforms-submit--read-more", "btn-link-read-more-primary");
    setupReadMoreHoverAnimation(submitBtn);
  };

  const setupSubmitArrowAnimation = (form) => {
    const submitBtn = getAnimatedSubmitButton(form);
    if (!submitBtn || submitBtn.dataset.arrowAnimReady === "1") return;
    submitBtn.dataset.arrowAnimReady = "1";

    if (form.closest(".contact-form-panel")) {
      setupReadMoreSubmitMarkup(submitBtn);
    }
  };

  const normalize = (value) =>
    (value || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, " ")
      .trim();

  const isRequiredField = (field, input, label) => {
    if (label?.querySelector(".wpforms-required-label")) return true;
    if (field?.classList.contains("wpforms-field-required")) return true;
    if (input?.classList.contains("wpforms-field-required")) return true;
    if (input?.required) return true;
    if (input?.getAttribute("aria-required") === "true") return true;
    return false;
  };

  const setupInputDisplay = (field, options = {}) => {
    const input = field.querySelector(
      'input[type="text"], input[type="email"], input[type="tel"], input:not([type]), textarea',
    );
    const label = field.querySelector(
      ".wpforms-field-label, legend.wpforms-field-label",
    );
    if (!input || !label) return;

    field.classList.add("wp-form__input-display-field");
    const rawLabel = label.textContent.replace(/\*/g, "").trim();
    const cleanLabel = options.transformLabel
      ? options.transformLabel(rawLabel, field)
      : rawLabel;

    let overlay = field.querySelector(".wp-form__input-display");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "wp-form__input-display";
      field.appendChild(overlay);
    }
    overlay.innerHTML = `${cleanLabel}${isRequiredField(field, input, label) ? " <mark>*</mark>" : ""}`;

    const updateState = () => {
      const value = (input.value || "").trim();
      field.classList.toggle("is-filled", value !== "");
    };

    updateState();
    if (input.dataset.wpformDisplayBound === "1") return;
    input.addEventListener("input", updateState);
    input.addEventListener("change", updateState);
    input.addEventListener("focus", updateState);
    input.addEventListener("blur", updateState);
    input.dataset.wpformDisplayBound = "1";
  };

  const setupSelectDisplay = (field, options = {}) => {
    const select = field.querySelector("select");
    const label = field.querySelector(
      ".wpforms-field-label, legend.wpforms-field-label",
    );
    if (!select || !label) return;

    field.classList.add("wp-form__select-display-field");
    const rawLabel = label.textContent.replace(/\*/g, "").trim();
    const cleanLabel = options.transformLabel
      ? options.transformLabel(rawLabel, field)
      : rawLabel;

    let overlay = field.querySelector(".wp-form__select-display");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "wp-form__select-display";
      field.appendChild(overlay);
    }
    overlay.innerHTML = `${cleanLabel}${isRequiredField(field, select, label) ? " <mark>*</mark>" : ""}`;

    const updateState = () => {
      const value = (select.value || "").trim();
      field.classList.toggle("is-filled", value !== "");
    };

    updateState();
    if (select.dataset.wpformDisplayBound === "1") return;
    select.addEventListener("change", updateState);
    select.dataset.wpformDisplayBound = "1";
  };

  const forceFieldVisible = (field) => {
    if (!field) return;
    if (
      field.classList.contains("wpforms-field-hidden") ||
      field.classList.contains("wpforms-hidden") ||
      field.classList.contains("wpforms-conditional-hide") ||
      field.classList.contains("wpforms-conditional-hidden") ||
      field.hasAttribute("hidden") ||
      field.getAttribute("aria-hidden") === "true"
    ) {
      return;
    }
    field.style.removeProperty("display");
  };

  const getFieldLabelText = (field) => {
    const label = field.querySelector(
      ".wpforms-field-label, legend.wpforms-field-label",
    );
    if (!label) return "";
    return normalize((label.textContent || "").replace(/\*/g, "").trim());
  };

  const hasEmailSignature = (field) => {
    if (!field) return false;
    if (field.classList.contains("wpforms-field-email")) return true;
    if (field.querySelector('input[type="email"]')) return true;
    if (
      field.querySelector(
        'input[name*="email" i], input[id*="email" i], input[placeholder*="email" i]',
      )
    ) {
      return true;
    }
    return false;
  };

  const roleClassMap = {
    country: "hc-role-country",
    questionType: "hc-role-question-type",
    name: "hc-role-name",
    email: "hc-role-email",
    contactNumber: "hc-role-contact-number",
    companyName: "hc-role-company-name",
    companyWebsite: "hc-role-company-website",
    message: "hc-role-message",
  };

  const clearRoleClasses = (field) => {
    if (!field) return;
    field.classList.remove("hc-field-core");
    Object.values(roleClassMap).forEach((cls) => field.classList.remove(cls));
  };

  const setRole = (field, role, isCore = true) => {
    if (!field) return;
    clearRoleClasses(field);
    if (roleClassMap[role]) field.classList.add(roleClassMap[role]);
    if (isCore) field.classList.add("hc-field-core");
  };

  const detectFieldRole = (field) => {
    if (!field) return "";

    // Preferred mechanism: explicit role class on the WPForms field.
    const explicitRole = Object.entries(roleClassMap).find(([, className]) =>
      field.classList.contains(className),
    );
    if (explicitRole) return explicitRole[0];

    if (field.querySelector("textarea")) {
      return "message";
    }
    if (field.classList.contains("wpforms-field-phone") || field.querySelector('input[type="tel"]')) {
      return "contactNumber";
    }
    if (field.classList.contains("wpforms-field-url") || field.querySelector('input[type="url"]')) {
      return "companyWebsite";
    }
    if (hasEmailSignature(field)) {
      return "email";
    }
    if (field.querySelector("select")) return "";

    return "";
  };

  const getAllRealFields = (container) => {
    const topFields = Array.from(container.children).filter(
      (node) => node.classList && node.classList.contains("wpforms-field"),
    );

    const layoutField = topFields.find((field) =>
      field.classList.contains("wpforms-field-layout"),
    );

    if (!layoutField) {
      return {
        layoutField: null,
        fields: topFields,
      };
    }

    const nestedFields = Array.from(
      layoutField.querySelectorAll(".wpforms-field"),
    ).filter((field) => !field.classList.contains("wpforms-field-layout"));

    const otherTopFields = topFields.filter((field) => field !== layoutField);

    return {
      layoutField,
      fields: [...nestedFields, ...otherTopFields],
    };
  };

  const createColumnsWrap = () => {
    const columnsWrap = document.createElement("div");
    columnsWrap.className = "hc-columns";

    const leftCol = document.createElement("div");
    leftCol.className = "hc-col hc-col-left";

    const rightCol = document.createElement("div");
    rightCol.className = "hc-col hc-col-right";

    columnsWrap.appendChild(leftCol);
    columnsWrap.appendChild(rightCol);

    return { columnsWrap, leftCol, rightCol };
  };

  const resetCustomColumns = (container) => {
    if (!container) return;

    const wraps = container.querySelectorAll(":scope > .hc-columns");
    wraps.forEach((wrap) => {
      const fields = Array.from(wrap.querySelectorAll(".wpforms-field"));
      fields.forEach((field) => {
        container.insertBefore(field, wrap);
      });
      wrap.remove();
    });
  };

  const isStructuredLayoutHealthy = (form) => {
    if (!form) return false;
    const container = form.querySelector(".wpforms-field-container");
    if (!container) return false;

    const columns = container.querySelector(":scope > .hc-columns");
    if (!columns) return false;
    if (!columns.querySelector(".hc-col-left")) return false;
    if (!columns.querySelector(".hc-col-right")) return false;
    if (!columns.querySelector(".hc-col-right .hc-role-message")) return false;

    const submitContainer = form.querySelector(".wpforms-submit-container");
    if (submitContainer && submitContainer.parentElement !== container) {
      return false;
    }

    return true;
  };

  const setupContactTypeState = (form, radioFields) => {
    if (!form || !radioFields.length) return;

    const getRadioValue = (input) => normalize(input?.value || "");
    const getRadioLabel = (input) => {
      if (!input) return "";
      const item = input.closest("li");
      const label = item
        ? item.querySelector("label")
        : input.nextElementSibling;
      return normalize(label?.textContent || "");
    };

    const isCompanyRadio = (input) => {
      const value = getRadioValue(input);
      const label = getRadioLabel(input);
      return value === "company" || label.includes("company");
    };

    const isIndividualRadio = (input) => {
      const value = getRadioValue(input);
      const label = getRadioLabel(input);
      return value === "individual" || label.includes("individual");
    };

    let contactTypeField = radioFields.find((field) => {
      const inputs = field.querySelectorAll('input[type="radio"]');
      if (!inputs.length) return false;

      return Array.from(inputs).some(
        (input) => isCompanyRadio(input) || isIndividualRadio(input),
      );
    });

    if (!contactTypeField) {
      contactTypeField = radioFields.find(
        (field) => field.querySelectorAll('input[type="radio"]').length >= 2,
      );
    }

    if (!contactTypeField) return;

    const inputs = contactTypeField.querySelectorAll('input[type="radio"]');
    if (!inputs.length) return;
    if (contactTypeField.dataset.wpformContactStateBound === "1") return;

    const updateState = () => {
      const checked = Array.from(inputs).find((input) => input.checked);
      const checkedIndex = checked ? Array.from(inputs).indexOf(checked) : -1;

      form.classList.remove("is-individual", "is-company");

      if (checked && isCompanyRadio(checked)) {
        form.classList.add("is-company");
        return;
      }

      if (checked && isIndividualRadio(checked)) {
        form.classList.add("is-individual");
        return;
      }

      // Fallback by common radio order when translation text is unavailable.
      if (checkedIndex === 0) {
        form.classList.add("is-individual");
        return;
      }

      if (checkedIndex === 1) {
        form.classList.add("is-company");
      }
    };

    inputs.forEach((input) => {
      input.addEventListener("change", updateState);
      input.addEventListener("click", updateState);
    });
    contactTypeField.dataset.wpformContactStateBound = "1";

    updateState();
  };

  const setupContactForm = (form) => {
    if (!form) return;

    const container = form.querySelector(".wpforms-field-container");
    if (!container) return;
    if (
      form.dataset.wpFormReady === "1" &&
      isStructuredLayoutHealthy(form)
    ) {
      return;
    }

    resetCustomColumns(container);

    const { layoutField, fields } = getAllRealFields(container);
    if (!fields.length) return;

    const byRole = {
      country: null,
      questionType: null,
      name: null,
      email: null,
      contactNumber: null,
      companyName: null,
      companyWebsite: null,
      message: null,
    };

    const radioFields = [];
    const checkboxFields = [];
    const htmlFields = [];
    const hiddenFields = [];
    const trailingFields = [];

    fields.forEach((field) => {
      if (field.classList.contains("wpforms-field-radio")) {
        radioFields.push(field);
        return;
      }
      if (field.classList.contains("wpforms-field-checkbox")) {
        checkboxFields.push(field);
        return;
      }
      if (field.classList.contains("wpforms-field-html")) {
        htmlFields.push(field);
        return;
      }
      if (field.classList.contains("wpforms-field-hidden")) {
        hiddenFields.push(field);
        return;
      }

      const role = detectFieldRole(field);
      if (role && !byRole[role]) {
        byRole[role] = field;
      } else {
        trailingFields.push(field);
      }
    });

    // Translation-safe fallback: assign missing roles by control type and order.
    const takeFirstBy = (predicate) => {
      const idx = trailingFields.findIndex((field) => predicate(field));
      if (idx < 0) return null;
      const [field] = trailingFields.splice(idx, 1);
      return field;
    };

    if (!byRole.country) {
      byRole.country = takeFirstBy((field) => field.querySelector("select"));
    }
    if (!byRole.questionType) {
      byRole.questionType = takeFirstBy((field) => field.querySelector("select"));
    }
    if (!byRole.message) {
      byRole.message = takeFirstBy((field) => field.querySelector("textarea"));
    }
    const popTextLike = () =>
      takeFirstBy((field) => field.querySelector('input[type="text"], input[type="email"], input[type="tel"], input[type="url"]'));
    const popEmailLike = () =>
      takeFirstBy((field) => {
        const input = field.querySelector('input[type="email"], input[type="text"]');
        if (!input) return false;
        const meta = `${input.name || ""} ${(input.id || "")} ${(input.placeholder || "")} ${input.getAttribute("autocomplete") || ""} ${input.getAttribute("inputmode") || ""}`.toLowerCase();
        return hasEmailSignature(field) || meta.includes("email") || meta.includes("mail");
      });
    const popPhoneLike = () =>
      takeFirstBy((field) => {
        const input = field.querySelector('input[type="tel"], input[type="text"]');
        if (!input) return false;
        const meta = `${input.name || ""} ${(input.id || "")} ${(input.placeholder || "")} ${input.getAttribute("autocomplete") || ""} ${input.getAttribute("inputmode") || ""}`.toLowerCase();
        if (meta.includes("email") || meta.includes("mail")) return false;
        return meta.includes("phone") || meta.includes("mobile") || meta.includes("tel");
      });
    const popWebsiteLike = () =>
      takeFirstBy((field) => {
        const input = field.querySelector('input[type="url"], input[type="text"]');
        if (!input) return false;
        const meta = `${input.name || ""} ${(input.id || "")} ${(input.placeholder || "")} ${input.getAttribute("autocomplete") || ""} ${input.getAttribute("inputmode") || ""}`.toLowerCase();
        return input.type === "url" || meta.includes("website") || meta.includes("url");
      });

    if (!byRole.name) byRole.name = popTextLike();
    if (!byRole.email) byRole.email = popEmailLike() || popTextLike();
    if (!byRole.contactNumber) byRole.contactNumber = popPhoneLike() || popTextLike();
    if (!byRole.companyName) byRole.companyName = popTextLike();
    if (!byRole.companyWebsite) byRole.companyWebsite = popWebsiteLike() || popTextLike();

    const getFieldMeta = (field) => {
      const input = field?.querySelector("input, textarea, select");
      return `${input?.type || ""} ${input?.name || ""} ${input?.id || ""} ${input?.placeholder || ""} ${input?.getAttribute("autocomplete") || ""} ${input?.getAttribute("inputmode") || ""}`.toLowerCase();
    };
    const looksLikeEmail = (field) => hasEmailSignature(field) || /(^|\W)(email|e-mail|mail)(\W|$)/.test(getFieldMeta(field));
    const looksLikePhone = (field) => /(^|\W)(phone|mobile|tel|telephone)(\W|$)/.test(getFieldMeta(field)) || /(^|\W)tel(\W|$)/.test(getFieldMeta(field));

    if (byRole.email && byRole.contactNumber) {
      const emailLooksEmail = looksLikeEmail(byRole.email);
      const emailLooksPhone = looksLikePhone(byRole.email);
      const contactLooksEmail = looksLikeEmail(byRole.contactNumber);
      const contactLooksPhone = looksLikePhone(byRole.contactNumber);
      const shouldSwap =
        (!emailLooksEmail && contactLooksEmail) ||
        (emailLooksPhone && !contactLooksPhone);

      if (shouldSwap) {
        const tmp = byRole.email;
        byRole.email = byRole.contactNumber;
        byRole.contactNumber = tmp;
      }
    }

    [
      [byRole.country, "country"],
      [byRole.questionType, "questionType"],
      [byRole.name, "name"],
      [byRole.email, "email"],
      [byRole.contactNumber, "contactNumber"],
      [byRole.companyName, "companyName"],
      [byRole.companyWebsite, "companyWebsite"],
      [byRole.message, "message"],
    ].forEach(([field, role]) => {
      if (!field) return;
      forceFieldVisible(field);
      setRole(field, role, true);

      if (field.querySelector("select")) {
        setupSelectDisplay(field);
      } else {
        setupInputDisplay(field);
      }
    });

    if (layoutField) {
      layoutField.style.display = "none";
      layoutField.setAttribute("aria-hidden", "true");
    }

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    radioFields.forEach((field) => {
      forceFieldVisible(field);
      container.appendChild(field);
    });

    const { columnsWrap, leftCol, rightCol } = createColumnsWrap();

    [
      byRole.country,
      byRole.name,
      byRole.email,
      byRole.contactNumber,
      byRole.companyName,
      byRole.companyWebsite,
    ].forEach((field) => {
      if (field) leftCol.appendChild(field);
    });

    [byRole.questionType, byRole.message].forEach((field) => {
      if (field) rightCol.appendChild(field);
    });

    container.appendChild(columnsWrap);

    trailingFields.forEach((field) => {
      forceFieldVisible(field);
      clearRoleClasses(field);
      container.appendChild(field);
    });

    checkboxFields.forEach((field) => {
      forceFieldVisible(field);
      container.appendChild(field);
    });

    htmlFields.forEach((field) => {
      forceFieldVisible(field);
      container.appendChild(field);
    });

    hiddenFields.forEach((field) => {
      container.appendChild(field);
    });

    const submitContainer = form.querySelector(".wpforms-submit-container");
    if (submitContainer) {
      container.appendChild(submitContainer);
    }

    setupContactTypeState(form, radioFields);
    setupSubmitArrowAnimation(form);
    form.dataset.wpFormReady = "1";
  };

  const setupWarrantyForm = (form) => {
    if (!form) return;
    const warrantyLabelTransform = (label) => {
      if (normalize(label) === "product information") {
        return "Product Model / Name";
      }

      return label;
    };

    const fields = Array.from(form.querySelectorAll(".wpforms-field"));
    fields.forEach((field) => {
      const headingLabel = field.querySelector(
        ".wpforms-field-label, legend.wpforms-field-label",
      );
      const headingText = headingLabel
        ? normalize(headingLabel.textContent.replace(/\*/g, "").trim())
        : "";

      const isWarrantySectionHeading =
        headingText === "product information" ||
        headingText.includes("purchase date") ||
        headingText.includes("applicant information");
      const isProblemDescriptionField = headingText.includes("problem description");

      field.classList.toggle("hc-role-message", isProblemDescriptionField);

      if (headingLabel) {
        headingLabel.classList.toggle(
          "warranty-heading-label",
          isWarrantySectionHeading,
        );
      }

      if (
        headingLabel &&
        headingText === "product information"
      ) {
        headingLabel
          .querySelectorAll(".wpforms-required-label")
          .forEach((node) => node.remove());
      }

      if (
        field.classList.contains("wpforms-field-checkbox") ||
        field.classList.contains("wpforms-field-radio") ||
        field.classList.contains("wpforms-field-file-upload") ||
        field.classList.contains("wpforms-field-html") ||
        field.classList.contains("wpforms-field-hidden") ||
        field.classList.contains("wpforms-field-layout")
      ) {
        return;
      }
      let hasDateTokenSelect = false;
      const selects = field.querySelectorAll("select");
      selects.forEach((select) => {
        const placeholderOption =
          select.querySelector('option[value=""]') ||
          select.querySelector('option[value="0"]') ||
          select.querySelector("option[disabled][selected]") ||
          select.querySelector("option:first-child");
        if (!placeholderOption) return;

        const rawLabel = (placeholderOption.textContent || "").replace(/\*/g, "");
        const compactLabel = rawLabel.replace(/[^a-z]/gi, "").toLowerCase();
        if (!["dd", "mm", "yyyy", "day", "month", "year"].includes(compactLabel)) return;

        hasDateTokenSelect = true;
        placeholderOption.textContent = `${rawLabel.trim()}*`;

        let rowWrap = select.parentElement;
        if (rowWrap && rowWrap.querySelectorAll("select").length > 1) {
          const ownWrap = document.createElement("div");
          ownWrap.className = "wp-form__date-overlay-wrap";
          rowWrap.insertBefore(ownWrap, select);
          ownWrap.appendChild(select);
          rowWrap = ownWrap;
        }
        if (!rowWrap) return;
        if (window.getComputedStyle(rowWrap).position === "static") {
          rowWrap.style.position = "relative";
        }

        let nativeOverlay = Array.from(rowWrap.children).find(
          (node) => node.classList?.contains("wp-form__date-overlay"),
        );
        if (!nativeOverlay) {
          nativeOverlay = document.createElement("div");
          nativeOverlay.className = "wp-form__date-overlay";
          rowWrap.appendChild(nativeOverlay);
        }
        if (nativeOverlay) {
          nativeOverlay.innerHTML = `${rawLabel.trim()} <mark>*</mark>`;
        }

        const syncDateSelectState = () => {
          const currentValue = (select.value || "").trim().toLowerCase();
          const isEmpty =
            currentValue === "" ||
            currentValue === "0" ||
            currentValue === "mm" ||
            currentValue === "dd" ||
            currentValue === "yyyy" ||
            currentValue === "day" ||
            currentValue === "month" ||
            currentValue === "year";

          if (!nativeOverlay) {
            select.classList.remove("is-empty");
            return;
          }

          select.classList.toggle("is-empty", isEmpty);
          nativeOverlay.classList.toggle("is-hidden", !isEmpty);
        };

        if (!select.dataset.warrantyDateInit) {
          select.dataset.warrantyDateInit = "1";
          select.addEventListener("change", syncDateSelectState);
          select.addEventListener("input", syncDateSelectState);
        }

        syncDateSelectState();
      });

      if (hasDateTokenSelect) {
        field.classList.remove("wp-form__select-display-field");
        field
          .querySelectorAll(".wp-form__select-display")
          .forEach((node) => node.remove());
        return;
      }

      if (field.querySelector("select")) {
        setupSelectDisplay(field, { transformLabel: warrantyLabelTransform });
        return;
      }

      if (field.querySelector("input, textarea")) {
        setupInputDisplay(field, { transformLabel: warrantyLabelTransform });
      }
    });

    setupSubmitArrowAnimation(form);
  };

  Alpine.directive("wpform", (el) => {
    const run = () => {
      const forms = el.querySelectorAll(".wpforms-container .wpforms-form");
      forms.forEach((form) => {
        setupContactForm(form);
      });
    };

    run();

    const observer = new MutationObserver(() => {
      run();
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
    });
  });

  Alpine.directive("warrantyform", (el) => {
    const run = () => {
      const forms = el.querySelectorAll(".wpforms-container .wpforms-form");
      forms.forEach((form) => {
        setupWarrantyForm(form);
      });
    };

    run();

    const observer = new MutationObserver((mutations) => {
      const shouldRun = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof HTMLElement)) return false;
          return (
            node.matches(
              ".wpforms-form, .wpforms-field, .wpforms-field-container, select, input, textarea",
            ) ||
            node.querySelector?.(
              ".wpforms-form, .wpforms-field, .wpforms-field-container, select, input, textarea",
            )
          );
        }),
      );
      if (shouldRun) run();
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
    });
  });

  const isWarrantyContainer = (container) => {
    if (!(container instanceof HTMLElement)) return false;

    if (
      container.closest(
        ".warranty-reg, [data-wpform-style='warranty'], [data-form-style='warranty']",
      )
    ) {
      return true;
    }

    const text = (container.textContent || "").toLowerCase();
    return (
      text.includes("product information") &&
      (text.includes("purchase date") || text.includes("purchase date & channel"))
    );
  };

  const ensureFormHost = (form, isWarranty) => {
    if (!(form instanceof HTMLElement)) return null;

    const container = form.closest(".wpforms-container");
    const host =
      container?.closest(".contact-form-panel") ||
      container?.parentElement ||
      form.parentElement ||
      form;

    if (!(host instanceof HTMLElement)) return null;

    host.classList.add("contact-form-panel");
    host.setAttribute(isWarranty ? "x-warrantyform" : "x-wpform", "");
    host.removeAttribute(isWarranty ? "x-wpform" : "x-warrantyform");

    return host;
  };

  const processForms = (root = document) => {
    if (!root) return;

    const forms = [];
    if (root instanceof HTMLElement && root.matches(".wpforms-container .wpforms-form")) {
      forms.push(root);
    }
    if (root.querySelectorAll) {
      forms.push(...root.querySelectorAll(".wpforms-container .wpforms-form"));
    }

    forms.forEach((form) => {
      const container = form.closest(".wpforms-container");
      const isWarranty = isWarrantyContainer(container || form);
      const host = ensureFormHost(form, isWarranty);

      if (window.Alpine?.initTree && host) {
        window.Alpine.initTree(host);
      }

      if (isWarranty) {
        setupWarrantyForm(form);
      } else {
        setupContactForm(form);
      }
    });
  };

  const bootAutoProcess = () => {
    processForms(document);

    if (!document.body || document.body.dataset.wpformsAutoObserveReady === "1") return;
    document.body.dataset.wpformsAutoObserveReady = "1";

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(".wpforms-form, .wpforms-container")) {
            processForms(node.parentElement || node);
            return;
          }
          if (node.querySelector(".wpforms-form, .wpforms-container")) {
            processForms(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  document.addEventListener("alpine:initialized", bootAutoProcess);
  document.addEventListener("DOMContentLoaded", bootAutoProcess);
  window.addEventListener("load", () => processForms(document));

  if (document.readyState !== "loading") {
    setTimeout(bootAutoProcess, 0);
  }
}
